import { useCallback, useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';
import { useDarkMode } from '../../recoil';
import {
  getAnnouncements,
  type AnnouncementSeverity,
  type SiteAnnouncement,
} from '../../services/site-content.service';
import { getAccessToken } from '../../utils/tokens';

/**
 * Announcements published from the admin panel, shown as a thin strip above the
 * trading screen.
 *
 * Deliberately narrower and quieter than the customer portal's version: this sits
 * above a live chart and order book, so it is one line, collapses to the highest
 * priority notice, and never stacks. Banners are not rendered here at all.
 *
 * The server applies the isActive flag, the schedule window and the audience, so
 * whatever comes back is meant to be on screen.
 */

const DISMISSED_KEY = 'rva.dismissedAnnouncements';
const REFRESH_MS = 5 * 60 * 1000;

/** Shared with the customer portal, so dismissing in one app is not undone by the
 * other reading a different key. They are separate origins and therefore separate
 * localStorage, but keeping the key identical avoids a surprise if that changes. */
function readDismissed(): string[] {
  try {
    const parsed = JSON.parse(localStorage.getItem(DISMISSED_KEY) || '[]');
    return Array.isArray(parsed) ? parsed.filter((id) => typeof id === 'string') : [];
  } catch {
    return [];
  }
}

/*
 * Severity is expressed as a class, not as Tailwind colour utilities. This project's
 * tailwind.config.js replaces theme.borderColor and theme.fontSize rather than
 * extending them, and uses opacity steps outside the default scale, so classes like
 * `bg-blue-500/12` and `border-blue-500/40` were never generated -- the bar rendered
 * transparent with Bootstrap's default text colour. The colours live in AppStyles.
 */
const SEVERITY_CLASS: Record<AnnouncementSeverity, string> = {
  INFO: 'ann-info',
  SUCCESS: 'ann-success',
  WARNING: 'ann-warning',
  CRITICAL: 'ann-critical',
};

/**
 * Resolves a stored CTA target and drops anything that is not http(s).
 *
 * A root-relative path is resolved against this app's own origin, so the same stored
 * row works on localhost:4000 and on the deployed trading host.
 */
function safeHref(url: string | null): string | null {
  if (!url) return null;
  try {
    const parsed = new URL(url, window.location.origin);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:' ? parsed.href : null;
  } catch {
    return null;
  }
}

function isExternalHref(href: string): boolean {
  try {
    return new URL(href, window.location.origin).origin !== window.location.origin;
  } catch {
    return false;
  }
}

export default function AnnouncementBar() {
  const [darkMode] = useDarkMode();
  const [dismissed, setDismissed] = useState<string[]>(readDismissed);

  // Signed-in state decides the audience. Read at render rather than held in state
  // because this app keeps the token in storage, not in a store.
  const isAuthenticated = !!getAccessToken();

  const { data } = useSWR(
    ['trade/announcements', isAuthenticated],
    () => getAnnouncements(isAuthenticated),
    { refreshInterval: REFRESH_MS, revalidateOnFocus: true, shouldRetryOnError: false },
  );

  // Forget ids the server no longer returns, so the list cannot grow without bound.
  useEffect(() => {
    if (!data?.data) return;
    const live = new Set(data.data.map((item) => item.id));
    setDismissed((prev) => {
      const kept = prev.filter((id) => live.has(id));
      if (kept.length === prev.length) return prev;
      try {
        localStorage.setItem(DISMISSED_KEY, JSON.stringify(kept));
      } catch {
        /* storage unavailable */
      }
      return kept;
    });
  }, [data]);

  const dismiss = useCallback((id: string) => {
    setDismissed((prev) => {
      if (prev.includes(id)) return prev;
      const next = [...prev, id];
      try {
        localStorage.setItem(DISMISSED_KEY, JSON.stringify(next));
      } catch {
        /* storage unavailable */
      }
      return next;
    });
  }, []);

  /*
   * One notice only. The API already orders by priority descending, so the first
   * surviving row is the most important one.
   */
  const announcement: SiteAnnouncement | undefined = useMemo(
    () =>
      (data?.data ?? []).find(
        (item) => !(item.isDismissible && dismissed.includes(item.id)),
      ),
    [data, dismissed],
  );

  if (!announcement) return null;

  const href = safeHref(announcement.ctaUrl);
  const external = href ? isExternalHref(href) : false;

  return (
    <div
      role="status"
      className={`announcement-slot flex items-center gap-2 ${SEVERITY_CLASS[announcement.severity]}${
        darkMode ? '' : ' is-light'
      }`}
    >
      <span className="announcement-slot__title">{announcement.title}</span>
      {/* Text node: the body is plain text by design and is never rendered as HTML. */}
      <span className="announcement-slot__body" title={announcement.body}>
        {announcement.body}
      </span>
      {href && announcement.ctaLabel && (
        <a
          className="announcement-slot__cta"
          href={href}
          target={external ? '_blank' : undefined}
          rel={external ? 'noreferrer noopener' : undefined}
        >
          {announcement.ctaLabel}
        </a>
      )}
      {announcement.isDismissible && (
        <button
          type="button"
          className="announcement-slot__close"
          onClick={() => dismiss(announcement.id)}
          aria-label={`Dismiss: ${announcement.title}`}
        >
          &times;
        </button>
      )}
    </div>
  );
}
